import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Eye, Warning, ShieldWarning, Info, X, ArrowsClockwise, CaretRight, Funnel } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SEV_STYLES = {
  critical: { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500', text: 'text-red-700', badge: 'bg-red-100 text-red-700', icon: ShieldWarning },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', icon: Warning },
  info: { bg: 'bg-zinc-50', border: 'border-zinc-200', dot: 'bg-zinc-400', text: 'text-zinc-600', badge: 'bg-zinc-100 text-zinc-600', icon: Info },
};

export const ObserverInsights = ({ projectId, compact = false }) => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      if (projectId) {
        const { data } = await axios.get(`${BACKEND}/api/projects/${projectId}/observer/alerts`, { withCredentials: true });
        setAlerts(data);
      } else {
        const [alertsRes, summaryRes] = await Promise.all([
          axios.get(`${BACKEND}/api/observer/alerts?limit=50`, { withCredentials: true }),
          axios.get(`${BACKEND}/api/observer/alerts/summary`, { withCredentials: true }),
        ]);
        setAlerts(alertsRes.data);
        setSummary(summaryRes.data);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleDismiss = async (alertId) => {
    try {
      await axios.put(`${BACKEND}/api/observer/alerts/${alertId}/dismiss`, {}, { withCredentials: true });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      toast.success('Alert dismissed');
    } catch { toast.error('Failed to dismiss'); }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/observer/run-scan`, {}, { withCredentials: true });
      toast.success(`Scan complete: ${data.alerts_created} alert(s), ${data.tasks_escalated || 0} task(s) escalated`);
      fetchAlerts();
    } catch { toast.error('Scan failed'); }
    setScanning(false);
  };

  const filtered = filter === 'all' ? alerts : alerts.filter(a => a.severity === filter);

  if (loading) return <div className="text-xs text-zinc-400 py-4 text-center">Loading insights...</div>;

  // Compact mode for ProjectHub
  if (compact) {
    if (alerts.length === 0) return null;
    const critCount = alerts.filter(a => a.severity === 'critical').length;
    const warnCount = alerts.filter(a => a.severity === 'warning').length;

    return (
      <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-4" data-testid="project-observer-alerts">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Eye size={16} weight="fill" className="text-blue-600" />
            <span className="text-xs font-bold text-zinc-800">Observer Insights</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-500">{alerts.length}</span>
            {critCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">{critCount} critical</span>}
            {warnCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{warnCount} warning</span>}
          </div>
        </div>
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {alerts.slice(0, 8).map(alert => {
            const s = SEV_STYLES[alert.severity] || SEV_STYLES.info;
            return (
              <div key={alert.id} className={`flex items-start gap-2 p-2.5 rounded-md ${s.bg} border ${s.border}`} data-testid={`alert-${alert.id}`}>
                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${s.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] font-semibold uppercase ${s.text}`}>{alert.label}</span>
                  </div>
                  <p className="text-[11px] text-zinc-600 mt-0.5 leading-relaxed">{alert.detail}</p>
                  <p className="text-[10px] text-zinc-400 mt-1 italic">{alert.suggestion}</p>
                </div>
                <button onClick={() => handleDismiss(alert.id)} className="text-zinc-400 hover:text-zinc-600 shrink-0" data-testid={`dismiss-${alert.id}`}>
                  <X size={12} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Full mode for Command Center
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-4" data-testid="observer-insights-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Eye size={18} weight="fill" className="text-blue-600" />
          <span className="text-sm font-bold text-zinc-800">AI Observer Insights</span>
          {summary && (
            <div className="flex items-center gap-1.5 ml-2">
              {summary.counts.critical > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">{summary.counts.critical} Critical</span>}
              {summary.counts.warning > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{summary.counts.warning} Warning</span>}
              {summary.counts.info > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 font-semibold">{summary.counts.info} Info</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Filter */}
          <div className="flex items-center gap-1 border border-zinc-200 rounded-md overflow-hidden" data-testid="observer-filter">
            {['all', 'critical', 'warning', 'info'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-2.5 py-1 text-[10px] font-medium ${filter === f ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:bg-zinc-50'}`}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <button
            onClick={handleScan}
            disabled={scanning}
            className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50"
            data-testid="observer-run-scan"
          >
            <ArrowsClockwise size={12} className={scanning ? 'animate-spin' : ''} />
            {scanning ? 'Scanning...' : 'Scan Now'}
          </button>
        </div>
      </div>

      {/* Top Projects with alerts */}
      {summary?.top_projects?.length > 0 && (
        <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
          {summary.top_projects.map((p, i) => (
            <button
              key={i}
              onClick={() => navigate(`/projects/${p.project_id}`)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] bg-zinc-50 border border-zinc-200 rounded-md hover:bg-zinc-100 shrink-0"
              data-testid={`observer-project-${i}`}
            >
              <span className="font-semibold text-zinc-700 truncate max-w-[120px]">{p.project_name}</span>
              {p.critical_count > 0 && <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[9px] font-bold">{p.critical_count}</span>}
              <span className="text-zinc-400">{p.alert_count} alerts</span>
              <CaretRight size={10} className="text-zinc-400" />
            </button>
          ))}
        </div>
      )}

      {/* Alert List */}
      {filtered.length === 0 ? (
        <div className="text-center py-8 text-xs text-zinc-400">
          {alerts.length === 0 ? 'No active alerts — system is healthy' : `No ${filter} alerts`}
        </div>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {filtered.map(alert => {
            const s = SEV_STYLES[alert.severity] || SEV_STYLES.info;
            const SevIcon = s.icon;
            return (
              <div key={alert.id} className={`flex items-start gap-3 p-3 rounded-lg ${s.bg} border ${s.border} transition-all hover:shadow-sm`} data-testid={`alert-${alert.id}`}>
                <SevIcon size={16} weight="fill" className={`${s.text} mt-0.5 shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${s.badge}`}>{alert.severity}</span>
                    <span className="text-xs font-semibold text-zinc-800">{alert.label}</span>
                    {alert.occurrence_count > 1 && <span className="text-[9px] text-zinc-400">x{alert.occurrence_count}</span>}
                  </div>
                  <p className="text-[11px] text-zinc-600 mt-1 leading-relaxed">{alert.detail}</p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <p className="text-[10px] text-zinc-400 italic flex-1">{alert.suggestion}</p>
                    {alert.task_id && (
                      <button
                        onClick={() => navigate('/action-center')}
                        className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded hover:bg-emerald-100 shrink-0"
                        data-testid={`task-link-${alert.id}`}
                      >
                        {alert.task_id}
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/projects/${alert.project_id}`)}
                      className="text-[10px] text-blue-600 hover:underline shrink-0"
                    >
                      View Project
                    </button>
                  </div>
                </div>
                <button onClick={() => handleDismiss(alert.id)} className="text-zinc-400 hover:text-zinc-600 shrink-0 p-1" data-testid={`dismiss-${alert.id}`}>
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
